import { DomainEntityWithRights, Identifier } from "@/types/domain";
import { LocaleMessageType } from "@/types/locale";
import React, { useCallback, useMemo } from "react";
import { useLocale, useTranslations } from "next-intl";
import { ClosableAlert } from "@components/alerts/closable-alert";
import { DeleteButton } from "@components/common/buttons/delete-button";
import { AlertDialogWrapper } from "@components/common/containers/alert-dialog-wrapper";
import { ButtonWithTooltipProps } from "@components/ui/custom/button-with-tooltip";
import { localizeFrom } from "@lib/i18n/utils";
import { useNotificationToasts } from "@features/notifications/notifications-hooks";
import { useDeleteActivityMemberMutation } from "@features/proposals/proposals-api";
import { ActivityMemberEntityRightsTypes } from "@features/proposals/types/rights";
interface MemberDeleteBlockProps {
  member?: {
    id: Identifier;
    eventProfile?: {
      biography?: {
        content?: {
          data?: {
            firstName?: LocaleMessageType;
            lastName?: LocaleMessageType;
          };
        };
      };
    };
  } & DomainEntityWithRights<ActivityMemberEntityRightsTypes>;
  isSingleMember: boolean;
}

/**
 * The `MemberDeleteBlock` component is used to render a delete button for a specific member and
 * handle the member deletion process through a confirmation dialog.
 *
 * This component ensures the following:
 * - It verifies if the member has sufficient rights to be deleted.
 * - It prevents deletion when the `isSingleMember` flag is true to avoid accidental deletions.
 * - It displays a localized confirmation dialog with the member's name and additional warnings.
 * - It handles the deletion process by calling the corresponding mutation function.
 *
 * The display name of the member follows a prioritized resolution strategy:
 * 1. Full name (firstName + lastName) retrieved from the member's biography.
 * 2. Member ID used as a fallback if the full name is unavailable.
 *
 * Props:
 * - Includes properties from `MemberDeleteBlockProps`, `React.ComponentProps<"button">`, and
 *   variant properties for custom button styling.
 *
 * Notes:
 * - If the member does not exist or lacks delete permissions, the component returns null.
 * - The `isSingleMember` prop disables the delete button to prevent deletion when only one
 *   member exists.
 */
export const MemberDeleteBlock = ({
  member,
  isSingleMember,
  ...props
}: MemberDeleteBlockProps & ButtonWithTooltipProps) => {
  const {
    showSuccessDeleteToast
  } = useNotificationToasts();
  const t = useTranslations("features.proposals.ui.memberDeleteBlock");
  const tCommon = useTranslations("common");
  const locale = useLocale();
  const [deleteActivityMember, {
    isLoading: isDeleting
  }] = useDeleteActivityMemberMutation();

  /**
   * Computes the display name of the member for use in the confirmation dialog.
   *
   * The resolution strategy uses the following priority:
   * 1. Full name from biography (firstName + lastName)
   * 2. ID as fallback
   */
  const memberName = useMemo(() => {
    if (!member) return "";
    const firstName = member.eventProfile?.biography?.content?.data?.firstName ? localizeFrom(member.eventProfile.biography.content.data.firstName, locale) : "";
    const lastName = member.eventProfile?.biography?.content?.data?.lastName ? localizeFrom(member.eventProfile.biography.content.data.lastName, locale) : "";
    const fullName = [firstName, lastName].filter(Boolean).join(" ");
    return fullName || `#${member.id}`;
  }, [member, locale]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const handleDelete = useCallback(() => {
    // Guard: Ensure the member has a valid numeric ID.
    if (!member?.id || typeof member.id !== "number") return Promise.reject(new Error("Invalid member ID."));
    return deleteActivityMember({
      params: {
        memberId: member.id
      }
    }).unwrap().then(() => {
      showSuccessDeleteToast();
    });
  }, [deleteActivityMember, member?.id, showSuccessDeleteToast]);
  if (!member || !member.rights?.canDelete) return null; // Guard: Ensure the member can be deleted.

  return <AlertDialogWrapper trigger={<DeleteButton variant="outline" tooltipContent={t("title")} {...props} disabled={isSingleMember}>
          {tCommon("words.verbs.delete")}
        </DeleteButton>} title={t("title")} actionTitle={tCommon("words.verbs.delete")} closeTitle={tCommon("words.verbs.cancel")} actionDisabled={isDeleting} handlers={{
    onAction: handleDelete
  }}>
      <div className="space-y-4">
        {/* 
          Renders the confirmation description with support for HTML markup.
          The 'memberName' is injected into the translation string.
         */}
        <p className="text-sm" dangerouslySetInnerHTML={{
        __html: t.markup("description", {
          memberName,
          strong: chunks => `<b>${chunks}</b>`
        })
      }} />
        <ClosableAlert variant="destructive" description={t("warning")} className="border-destructive/50" />
      </div>
    </AlertDialogWrapper>;
};